import { useEffect, useMemo, useState, type KeyboardEvent } from "react";
import { isAxiosError } from "axios";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Callout,
  CodeBlock,
  Field,
  Icon,
  MetaRow,
  Progress,
  Rule,
  SectionHeader,
  Spinner,
  Stamp,
  Stat,
} from "../ui";
import { DESCRIPTORS, WHITELIST_DOMAINS } from "../data/mock";
import { useStartSearch as startSearchHook } from "../hooks/useStartSearch";
import { updateProjectDescriptors } from "../api/projects";
import { useJobPolling as jobPollingHook } from "../hooks/useJobPolling";
import type { Job, Project, SearchJobMetadata } from "../types";

interface DescriptorLine {
  key: string;
  group: "Fixo" | "Personalizado";
  query: string;
}

const MAX_EXTRA_DESCRIPTORS = 10;
const MAX_DESCRIPTOR_LEN = 200;

function searchMetadata(job: Job | undefined): Partial<SearchJobMetadata> {
  return (job?.metadata ?? {}) as Partial<SearchJobMetadata>;
}

function buildExtraQuery(apiName: string, token: string): string {
  return `"${apiName}" AND "${token}"`;
}

function messageFromApiDetail(detail: unknown): string | null {
  if (typeof detail === "string") return detail;
  if (detail !== null && typeof detail === "object") {
    const record = detail as Record<string, unknown>;
    if (typeof record.message === "string") return record.message;
    if (typeof record.detail === "string") return record.detail;
  }
  return null;
}

function parseErrorMessage(error: unknown): string {
  if (isAxiosError(error)) {
    const apiMessage = messageFromApiDetail(error.response?.data?.detail);
    if (apiMessage) return apiMessage;
  }
  const directMessage = messageFromApiDetail(error);
  return directMessage ?? "Nao foi possivel salvar os descritores personalizados.";
}

function validateDescriptor(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return "Descritor vazio.";
  if (trimmed.length > MAX_DESCRIPTOR_LEN) {
    return `Descritor com ate ${MAX_DESCRIPTOR_LEN} caracteres.`;
  }
  return null;
}

function hasDuplicateDescriptor(items: string[], value: string): boolean {
  const normalized = value.trim().toLowerCase();
  return items.some((item) => item.trim().toLowerCase() === normalized);
}

export interface Step1DescriptorsProps {
  project: Project;
  mock: boolean;
  onAdvance: (n: number) => void;
}

export function Step1Descriptors({ project, onAdvance }: Step1DescriptorsProps) {
  const qc = useQueryClient();
  const startSearch = startSearchHook(project.id);
  const [jobId, setJobId] = useState<string | null>(null);
  const job = jobPollingHook(jobId);
  const metadata = searchMetadata(job.data);

  const [descriptorDraft, setDescriptorDraft] = useState("");
  const [extraDescriptors, setExtraDescriptors] = useState<string[]>(
    project.extra_descriptors ?? [],
  );
  const [draftError, setDraftError] = useState<string | null>(null);
  const [syncError, setSyncError] = useState<string | null>(null);

  useEffect(() => {
    setExtraDescriptors(project.extra_descriptors ?? []);
  }, [project.id, project.extra_descriptors]);

  const canEditDescriptors = project.status === "created";
  const canStartSearch =
    project.status === "created" || (project.status === "failed" && project.current_step <= 1);

  const saveDescriptors = useMutation({
    mutationFn: (body: string[]) =>
      updateProjectDescriptors(project.id, { extra_descriptors: body }),
    onSuccess: (updatedProject, variables) => {
      setExtraDescriptors(updatedProject.extra_descriptors ?? variables);
      setSyncError(null);
      qc.invalidateQueries({ queryKey: ["project", project.id] });
    },
    onError: (error) => {
      setSyncError(parseErrorMessage(error));
    },
  });

  const apiName = project.drug_api_name;
  const fixedQueries = useMemo(
    () =>
      DESCRIPTORS.map((item) => ({
        key: `fixed-${item.id}`,
        group: "Fixo" as const,
        query: item.template.replace("{api_name}", apiName),
      })),
    [apiName],
  );

  const customQueries = useMemo(
    () =>
      extraDescriptors.map((item, i) => ({
        key: `custom-${item}-${i}`,
        group: "Personalizado" as const,
        query: buildExtraQuery(apiName, item),
      })),
    [apiName, extraDescriptors],
  );

  const queryList = useMemo<DescriptorLine[]>(
    () => [...fixedQueries, ...customQueries],
    [customQueries, fixedQueries],
  );

  const isTerminalFailed = project.status === "failed" || job.data?.status === "failed";
  const isRunning =
    !isTerminalFailed &&
    (startSearch.isPending ||
      project.status === "searching" ||
      job.data?.status === "queued" ||
      job.data?.status === "running");
  const isDone =
    !isTerminalFailed && (project.current_step >= 2 || job.data?.status === "success");
  const isSavingDescriptors = saveDescriptors.isPending;
  const progress = job.data?.progress ?? (project.status === "searching" ? 10 : 0);
  const progressMessage = job.data?.progress_message ?? "Buscando em fontes regulatorias";

  const totalResults = metadata.total_results ?? 0;
  const uniqueResults = metadata.unique_results ?? metadata.whitelist_filtered ?? 0;
  const searchedDomains = metadata.domains_searched ?? 0;
  const totalDescriptors = DESCRIPTORS.length + extraDescriptors.length;

  const persistDescriptors = async (next: string[]) => {
    if (!canEditDescriptors) return;
    await saveDescriptors.mutateAsync(next);
  };

  const buildNextDescriptorsFromDraft = (): string[] | null => {
    if (!canEditDescriptors) return null;
    const trimmed = descriptorDraft.trim();
    if (!trimmed) {
      setDescriptorDraft("");
      setDraftError(null);
      return null;
    }
    if (extraDescriptors.length >= MAX_EXTRA_DESCRIPTORS) {
      setDraftError(`Maximo de ${MAX_EXTRA_DESCRIPTORS} descritores.`);
      return null;
    }
    const validationError = validateDescriptor(trimmed);
    if (validationError) {
      setDraftError(validationError);
      return null;
    }
    if (hasDuplicateDescriptor(extraDescriptors, trimmed)) {
      setDraftError("Descritor ja incluido.");
      return null;
    }
    return [...extraDescriptors, trimmed];
  };

  const tryAddDescriptor = async () => {
    const next = buildNextDescriptorsFromDraft();
    if (!next) return;

    const previous = extraDescriptors;
    setExtraDescriptors(next);
    setDescriptorDraft("");
    setDraftError(null);
    setSyncError(null);
    try {
      await persistDescriptors(next);
    } catch {
      setExtraDescriptors(previous);
    }
  };

  const removeDescriptor = async (index: number) => {
    const previous = extraDescriptors;
    const next = extraDescriptors.filter((_, idx) => idx !== index);
    setExtraDescriptors(next);
    setSyncError(null);
    try {
      await persistDescriptors(next);
    } catch {
      setExtraDescriptors(previous);
    }
  };

  const onDescriptorKeyDown = async (event: KeyboardEvent<HTMLInputElement>) => {
    if (!canEditDescriptors || isSavingDescriptors) return;
    if (event.key === "Enter" || event.key === ",") {
      event.preventDefault();
      await tryAddDescriptor();
      return;
    }
    if (event.key === "Backspace" && !descriptorDraft && extraDescriptors.length) {
      event.preventDefault();
      await removeDescriptor(extraDescriptors.length - 1);
    }
  };

  const start = async () => {
    if (canEditDescriptors && descriptorDraft.trim()) {
      const next = buildNextDescriptorsFromDraft();
      if (!next) return;
      const previous = extraDescriptors;
      setExtraDescriptors(next);
      setDescriptorDraft("");
      setDraftError(null);
      setSyncError(null);
      try {
        await persistDescriptors(next);
      } catch {
        setExtraDescriptors(previous);
        return;
      }
    }

    const result = await startSearch.mutateAsync();
    setJobId(result.job_id);
  };

  const startDisabled =
    !canStartSearch ||
    startSearch.isPending ||
    isSavingDescriptors ||
    job.data?.status === "queued" ||
    job.data?.status === "running";

  return (
    <div className="anim-fade">
      <SectionHeader
        number={1}
        eyebrow="Etapa 01 - Busca"
        title="Descritores regulatorios"
        lede="Quatro consultas fixas, mais descritores personalizados para ampliar a cobertura."
      />

      <div className="grid grid-cols-12 gap-10 mb-14">
        <div className="col-span-12 lg:col-span-8">
          <Rule className="mb-6">Secao 1.1 - Consultas aplicadas</Rule>
          <ol className="space-y-4">
            {queryList.map((item, i) => (
              <li
                key={`${item.key}-${i}`}
                className="grid grid-cols-[3rem_1fr_5.25rem] gap-4 items-baseline"
              >
                <span className="step-num text-[28px] tabular text-[var(--ink-4)]">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <div>
                  <CodeBlock>{item.query}</CodeBlock>
                  <div className="caption mt-2">Operadores booleanos preservados</div>
                </div>
                <span className="text-right text-[11px] text-[var(--ink-3)] tabular">
                  {item.group}
                </span>
              </li>
            ))}
          </ol>

          {canEditDescriptors && (
            <div className="mt-10">
              <Field
                label="Descritores personalizados"
                hint="Pressione Enter ou virgula para adicionar"
                error={draftError ?? syncError ?? undefined}
              >
                <div
                  className="flex flex-wrap items-center gap-1.5 px-2 py-1.5 border border-[var(--ink)] bg-[var(--paper)] min-h-[2.5rem]"
                  onClick={(event) => {
                    const target = event.currentTarget.querySelector("input");
                    target?.focus();
                  }}
                >
                  {extraDescriptors.map((descriptor, index) => (
                    <span
                      key={`${descriptor}-${index}`}
                      className="inline-flex items-center gap-1 px-2 h-6 border border-[var(--ink)] bg-[var(--paper)] text-[12px] mono"
                    >
                      {descriptor}
                      <button
                        type="button"
                        onClick={() => {
                          void removeDescriptor(index);
                        }}
                        aria-label={`Remover descritor ${descriptor}`}
                        className="text-[var(--ink-3)] hover:text-[var(--strike)] -mr-0.5"
                      >
                        <Icon.X size={12} strokeWidth={2} />
                      </button>
                    </span>
                  ))}
                  <input
                    type="text"
                    value={descriptorDraft}
                    onChange={(event) => {
                      setDescriptorDraft(event.target.value);
                      if (draftError) setDraftError(null);
                    }}
                    onKeyDown={(event) => {
                      void onDescriptorKeyDown(event);
                    }}
                    onBlur={() => {
                      if (descriptorDraft.trim()) {
                        void tryAddDescriptor();
                      }
                    }}
                    placeholder={extraDescriptors.length === 0 ? "ex.: metabolito" : ""}
                    disabled={!canEditDescriptors || isSavingDescriptors}
                    className="min-w-[10rem] flex-1 bg-transparent py-1 text-[13px] outline-hidden placeholder:text-[var(--ink-4)] disabled:opacity-50"
                  />
                </div>
                <div className="caption mt-1">
                  {isSavingDescriptors
                    ? "Sincronizando descritores..."
                    : `${extraDescriptors.length}/${MAX_EXTRA_DESCRIPTORS} personalizados`}
                </div>
              </Field>
            </div>
          )}
        </div>

        <div className="col-span-12 lg:col-span-4">
          <Rule className="mb-6">Secao 1.2 - Cobertura</Rule>
          <div className="space-y-0">
            <MetaRow
              k="Descritores"
              v={
                <span className="serif text-[20px]">
                  {totalDescriptors} <span className="caption">consultas</span>
                </span>
              }
            />
            <MetaRow
              k="Dominios"
              v={
                <span>
                  {WHITELIST_DOMAINS.length} <span className="caption">possiveis</span>
                </span>
              }
            />
            <MetaRow k="Bases extras" v="PubChem - PubMed" mono />
            <MetaRow
              k="Distribuicao"
              v={`${DESCRIPTORS.length} fixos + ${extraDescriptors.length} extras`}
            />
            <MetaRow k="Idioma fonte" v="Ingles" />
          </div>
        </div>
      </div>

      <Rule className="mb-5">Secao 1.3 - Whitelist de dominios</Rule>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-t border-[var(--rule)] mb-14">
        {WHITELIST_DOMAINS.map((d, i) => (
          <div
            key={d.d}
            className={`flex items-baseline justify-between gap-4 py-2.5 px-4 border-b border-[var(--rule-2)] ${i % 3 !== 2 ? "lg:border-r" : ""} ${i % 2 !== 1 ? "md:border-r lg:border-r-0" : ""}`}
          >
            <div className="flex items-baseline gap-3 min-w-0">
              <span className="mono text-[10px] text-[var(--ink-4)] tabular shrink-0 w-5">
                {String(i + 1).padStart(2, "0")}
              </span>
              <span className="mono text-[12.5px] text-[var(--ink)] truncate">{d.d}</span>
            </div>
            <div className="flex items-baseline gap-3 shrink-0">
              <span className="caption">{d.t}</span>
              <span className="mono text-[11px] text-[var(--ink)] tabular w-6 text-right">
                w{d.w}
              </span>
            </div>
          </div>
        ))}
      </div>

      <Rule className="mb-6">Secao 1.4 - Execucao</Rule>
      {isRunning ? (
        <div className="anim-rise border border-[var(--ink)] p-10 bg-[var(--paper-2)]">
          <div className="flex items-baseline justify-between mb-6">
            <div className="flex items-baseline gap-3">
              <Spinner />
              <div className="display-4 leading-none">Buscando...</div>
            </div>
            <Stamp tone="ink" className="rotate-2">
              Em execucao
            </Stamp>
          </div>
          <Progress value={progress} label="Progresso" sub={progressMessage} />
          <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-6">
            <Stat value={totalResults || "-"} label="Resultados" sub="brutos" size="md" />
            <Stat
              value={searchedDomains || "-"}
              label="Dominios"
              sub={`de ${WHITELIST_DOMAINS.length} possiveis`}
              size="md"
            />
            <Stat
              value={totalDescriptors}
              label="Descritores"
              sub={`${DESCRIPTORS.length} fixos + ${extraDescriptors.length} extras`}
              size="md"
            />
            <Stat value={`${progress}%`} label="Progresso" size="md" />
          </div>
        </div>
      ) : isTerminalFailed ? (
        <div className="anim-rise border border-[var(--strike)] p-10 bg-[var(--paper-2)]">
          <div className="flex items-baseline gap-3 mb-4">
            <Icon.AlertCircle size={20} strokeWidth={2.5} className="text-[var(--strike)]" />
            <Stamp tone="strike">Falhou</Stamp>
          </div>
          <div className="display-3 mb-3">Busca regulatoria interrompida.</div>
          <p className="lede">
            {job.data?.error_message ??
              "A etapa nao encontrou fontes regulatorias suficientes para avancar."}
          </p>
          <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-6">
            <Stat value={totalResults || "-"} label="Brutas" sub="retornadas" size="md" />
            <Stat value={uniqueResults || "-"} label="Unicas" sub="apos dedup" size="md" />
            <Stat
              value={metadata.regulatory_sources ?? "-"}
              label="Regulatorias"
              sub="whitelist alta"
              size="md"
            />
            <Stat
              value={metadata.pubmed_sources ?? "-"}
              label="PubMed"
              sub="fallback cientifico"
              size="md"
            />
          </div>
          <div className="mt-8 flex justify-end">
            <Button
              variant="ink"
              size="lg"
              icon={startSearch.isPending ? Icon.Loader : Icon.RefreshCw}
              disabled={startDisabled}
              onClick={() => {
                void start();
              }}
            >
              Tentar novamente
            </Button>
          </div>
        </div>
      ) : isDone ? (
        <div className="anim-rise grid grid-cols-12 gap-10 items-end">
          <div className="col-span-12 lg:col-span-8">
            <div className="flex items-baseline gap-3 mb-3">
              <Icon.Check size={20} strokeWidth={2.5} className="text-[var(--ok)]" />
              <Stamp tone="ok">Concluida</Stamp>
            </div>
            <div className="display-2 mb-2">Busca concluida.</div>
            <p className="lede">
              {totalResults
                ? `${totalResults} fontes brutas retornadas. Apos dedup e ranking pelo peso de dominio, ${uniqueResults} unicas restaram.`
                : "A busca terminou. Avance para selecionar as fontes ranqueadas."}
            </p>
          </div>
          <div className="col-span-12 lg:col-span-4 flex flex-col gap-4 items-end">
            <div className="grid grid-cols-2 gap-6 border-l border-[var(--rule)] pl-6 text-right">
              <Stat value={totalResults || "-"} label="Brutas" sub="retornadas" size="md" />
              <Stat value={uniqueResults || "-"} label="Unicas" sub="apos dedup" size="md" />
            </div>
            <Button
              variant="ink"
              size="lg"
              iconRight={Icon.ArrowRight}
              onClick={() => onAdvance(2)}
            >
              Selecionar fontes
            </Button>
          </div>
        </div>
      ) : (
        <div className="anim-rise grid grid-cols-12 gap-10 items-end py-6">
          <div className="col-span-12 lg:col-span-8">
            <div className="display-2 mb-3">Pronto para iniciar.</div>
            <p className="lede">
              A busca consulta dominios oficiais com descritores fixos e personalizados.
              PubChem e PubMed rodam em paralelo. Tempo estimado: 2 a 3 minutos.
            </p>
            {startSearch.isError && (
              <div className="mt-6">
                <Callout tone="strike" icon={Icon.AlertCircle} title="Busca nao iniciada">
                  Verifique a conexao com a API e tente novamente.
                </Callout>
              </div>
            )}
            {saveDescriptors.isError && (
              <div className="mt-6">
                <Callout
                  tone="strike"
                  icon={Icon.AlertCircle}
                  title="Descritores nao sincronizados"
                >
                  {syncError || "Nao foi possivel salvar os descritores personalizados."}
                </Callout>
              </div>
            )}
          </div>
          <div className="col-span-12 lg:col-span-4 flex justify-end">
            <Button
              variant="ink"
              size="lg"
              icon={startSearch.isPending ? Icon.Loader : Icon.Play}
              disabled={startDisabled}
              onClick={() => {
                void start();
              }}
            >
              Iniciar busca
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
